import { Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { map, mergeMap, catchError, switchMap, startWith, skipWhile } from "rxjs/operators";
import { Observable, forkJoin, throwError, Subscription, interval, takeWhile, EMPTY, lastValueFrom, take, of } from "rxjs";
import * as _ from 'lodash';
import { FileService, ToastrService, CacheService } from 'mapfre-auditlib';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';

@Injectable({
  providedIn: 'root'
})

export class RelevantAspectService {

  className = 'RelevantAspectService'

  constructor(private http: HttpClient, private fileService: FileService,
    private toastService: ToastrService, private translateService: TranslateService, private cacheService: CacheService) { }

  /*
  * Relevant Aspects
  */
  getRelevantAspects(): any {
    return this.http.get(environment.base_URL + "/relevant-aspects")
  }

  getRelevantAspectById(id): Observable<any> {
    return this.http.get<any>(environment.base_URL + `/relevant-aspects/${id}`);
  }

  newRelevantAspect(assessment): Observable<any[]> {
    return this.http.post<any[]>(environment.base_URL + "/relevant-aspects", assessment);
  }

  newRelevantAspectWithFiles(assessment): Observable<any[]> {
    // PRE FILES INI
    let filesToUpload = _.remove(_.get(assessment, 'documents'), (f) => f instanceof File);
    // PRE FILES END
    //CHECK IF THERE ARE NEW FILES
    if (!_.isEmpty(filesToUpload)) {
      return this.newRelevantAspect(assessment).pipe(mergeMap((res: any) => {
        let observables = _.map(filesToUpload, (f) => this.fileService.upload(f, 'relevantAspect', res));
        return forkJoin(observables).pipe(map(resultFiles => {
          return res;
        }));
      }))
    }
    else {
      return this.newRelevantAspect(assessment)
    }
  }

  updateRelevantAspect(assessment, id) {
    return this.http.put<any[]>(environment.base_URL + `/relevant-aspects/${id}`, assessment)
  }

  updateRelevantAspectWithFiles(assessment, id): Observable<any[]> {
    // PRE FILES INI
    let filesToUpload = _.remove(_.get(assessment, 'documents'), (f) => f instanceof File);
    // PRE FILES END
    //CHECK IF THERE ARE NEW FILES
    if (!_.isEmpty(filesToUpload)) {
      return this.updateRelevantAspect(assessment, id).pipe(mergeMap((res: any) => {
        let observables = _.map(filesToUpload, (f) => this.fileService.upload(f, 'relevantAspect', res));
        return forkJoin(observables).pipe(map(resultFiles => {
          return res;
        }));
      }))
    }
    else {
      return this.updateRelevantAspect(assessment, id);
    }
  }

  deleteRelevantAspect(id): Observable<any> {
    return this.http.delete<any>(environment.base_URL + `/relevant-aspects/${id}`);
  }

  /*
   * Relevant Aspect Types
   */
  getRelevantAspectTypes(cache = true, cacheKey = "relevantAspectTypes"): any {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get(environment.base_URL + "/relevant-aspects-types").pipe(map((res) => {
      this.cacheService.updateCacheData(res, cacheKey)
      return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }


  /**
   * Comments type
   */

  getCommentsType(): any {
    return [{ title: "COMENTARIOS_NOVEDADES_LEGALES", form: "txtLegalNews" },
    { title: "COMENTARIOS_COLABORACIONES", form: "txtColab" },
    { title: "COMENTARIOS_AUDITORIA_EXTERNA", form: "txtAuditExt" },
    { title: "COMENTARIOS_REGULADORES", form: "txtRegulators" },
    { title: "COMENTARIOS_FRAUDE", form: "txtFrauds" },
    { title: "COMENTARIOS_FORMACION", form: "txtFormation" },
    { title: "COMENTARIOS_OTROS", form: "txtOthers" }];
  }



  exportRelevantAspects(key, params, data = null) {

    const user_locale = window.navigator.language;
    _.set(params, "locale", user_locale);

    _.set(params, "key", key + '/data.json');

    this.toastService.add({ severity: "info", summary: this.translateService.instant("TOAST.GENERAL.INFO_LOADING_TITLE"), detail: this.translateService.instant("TOAST.GENERAL.INFO_EXPORT"), life: 240000 });

    // Call aws gateway to generate pre signed url
    return this.http.get<any[]>(environment.base_URL + "/exported-url", { params })
      .pipe(mergeMap((res1: any) => {

        // Remove key paramenter and add url parameter
        _.unset(params, "key")
        _.set(params, "url", res1)

        // Call Mapgre Gateway to generate excel with exoprted jobs
        return this.http.get<any[]>(environment.base_URL + "/exported-aspects", { params })
          .pipe(mergeMap((res2: any) => {

            // Remove url paramenter and add key parameter
            let paramsExcel = {}
            _.set(paramsExcel, "keydata", key + '/data.json');
            _.set(paramsExcel, "keyexcel", key + '/export.xlsx');

            let method = 'get';
            let url = '/create-excel';

            return this.http[method]<any[]>(environment.base_URL + url, { params: paramsExcel })
              .pipe(mergeMap((res3: any) => {

                // Remove url paramenter and add key parameter
                let paramsDownload = {}
                _.set(paramsDownload, "onlyDownload", true)

                let body = {
                  key: key + '/export.xlsx'
                }

                // Download excel
                return this.http.post(environment.base_URL + `/download`, body, { params: paramsDownload })
                  .pipe(mergeMap((res4: any) => {

                    // Download file
                    let options = {
                      responseType: "blob" as 'json'
                    }

                    // Download file form S3
                    return this.http.get(res4, options);

                  }), map(res5 => {

                    //Clear toast notification
                    this.toastService.clear()
                    //Download generated Excel
                    FileSaver.saveAs(res5, `export.xlsx`);

                    // Delete file when downloaded
                    this.delete(key + '/export.xlsx').subscribe(() => { });
                    return res5;

                  }), catchError(err => {
                    this.toastService.add({
                      severity: "error",
                      summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
                      detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
                    });
                    return throwError(err);
                  }))


              }))
          }))

      }), map(res6 => {
        return res6;
      }), catchError<Observable<any>, any>((err) => {

        if(err.status != 500){ 

          let target = {};
          _.set(target, "keyFile", key + '/data.json');

          let ob: Observable<any>;
          let cont = 0;

          //Condiciones de timeout y repetición de request a check-files
          function checkConditions(response){
            if(cont <18 && response.statusCode === 404){
                cont++;
                return true;
            }else{
                cont++;
                return false
            }
          }

          //Multiple request to lambda chech-files. Stops when file has been found or timeout
          ob = interval(5000)
          .pipe(
            startWith(0),
            switchMap(() => this.http.get<any>(environment.base_URL + "/check-files", { params: target })),
            takeWhile(response => checkConditions(response), true)
          )

          forkJoin([ob]).subscribe(([check]) => {

            if(check.statusCode === 200){

                let paramsExcel = {}
                _.set(paramsExcel, "keydata", key + '/data.json');
                _.set(paramsExcel, "keyexcel", key + '/export.xlsx');

                let method = 'get';
                let url = '/create-excel';

                let result = forkJoin( [this.http[method]<any[]>(environment.base_URL + url, { params: paramsExcel })])

                return forkJoin([result]).subscribe(([resul1]) => {

                  // Remove url paramenter and add key parameter
                  let paramsDownload = {}
                  _.set(paramsDownload, "onlyDownload", true)

                  let body = {
                      key: key + '/export.xlsx'
                  }

                  // Download excel
                  let result2 = this.http.post(environment.base_URL + `/download`, body, { params: paramsDownload })
                      .pipe(mergeMap((res: any) => {
                        // Download file
                        let options = {
                            responseType: "blob" as 'json'
                        }

                        //Clear toast notification
                        this.toastService.clear()

                        // Download file form S3
                        return this.http.get(res, options);

                      }), map(res => {

                        // Delete file when downloaded
                        this.delete(key).subscribe(() => { });
                        return res;

                      }), catchError(err => {
                        this.toastService.add({
                            severity: "error",
                            summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
                            detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
                        });
                        return throwError(err);
                      }))

                      return forkJoin([result2]).subscribe(([resul2]) => {
                        //Download generated Excel
                        FileSaver.saveAs(resul2, `export.xlsx`);
                        return resul2;
                      });
                });
            }else{
                //Clear toast info notification
                this.toastService.clear()

                this.toastService.add({
                  severity: "error",
                  summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
                  detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TIMEOUT"), life: 5000
                });
            }
        });
        }else{
          //Clear toast info notification
          setTimeout(() => {
            this.toastService.clear()
          },4000)
        }

      return EMPTY;
     }))
  }

  delete(key): any {
    let params = {
      key
    }
    return this.http.post(environment.base_URL + "/delete", { params }).pipe(catchError(err => {
      this.toastService.add({
        severity: "error",
        summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
        detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
      });
      return throwError(err);
    }));
  }

  addRelevantAspectsType(params): any {
    return this.http.post(environment.base_URL + "/relevant-aspects-types", params);
  }

  updateRelevantAspectsType(params): any {
    return this.http.put(environment.base_URL + "/relevant-aspects-types", params);
  }

}

